/**
 * Service notes on a device page: what was done on site and when (antenna, LoRa card,
 * motherboard ...). FMS staff and installers write them; everyone who can see the device reads them.
 */
import { useState } from "react";
import { api, ApiError, type DeviceNote, type NoteKind } from "../api.ts";
import { NOTE_KINDS } from "../../../../packages/core/src/device-notes.ts";
import { Section, useLoad } from "../ui.tsx";

const dayText = (at: number) => new Date(at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export function DeviceNotes({ eui, myId, staff }: { eui: string; myId: string; staff: boolean }) {
  const notes = useLoad(() => api.deviceNotes(eui), [eui]);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<NoteKind>("visit");
  const [text, setText] = useState("");
  const [day, setDay] = useState(todayInput);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState<string | null>(null);
  const list = notes.data?.notes ?? [];
  const canWrite = notes.data?.canWrite ?? false;
  if (!notes.data || (!list.length && !canWrite)) return null;

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      // The day the work was done, at midday local time (so it reads as that day anywhere in Australia).
      const at = new Date(`${day}T12:00:00`).getTime();
      await api.addDeviceNote(eui, { kind, text: text.trim(), ...(Number.isFinite(at) ? { at: Math.min(at, Date.now()) } : {}) });
      setText("");
      setKind("visit");
      setDay(todayInput());
      setAdding(false);
      notes.reload();
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "Couldn't save the note.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (n: DeviceNote) => {
    try {
      await api.deleteDeviceNote(eui, n.id);
      notes.reload();
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "Couldn't delete the note.");
    }
    setConfirm(null);
  };

  return (
    <Section
      title="Service notes"
      right={
        canWrite && !adding ? (
          <button className="btn" onClick={() => setAdding(true)}>
            Add a note
          </button>
        ) : undefined
      }
    >
      {adding && (
        <form
          className="card"
          style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="frow">
            <div className="field">
              <label htmlFor="note-kind">What was done</label>
              <select id="note-kind" value={kind} onChange={(e) => setKind(e.target.value as NoteKind)}>
                {(Object.keys(NOTE_KINDS) as NoteKind[]).map((k) => (
                  <option key={k} value={k}>
                    {NOTE_KINDS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="note-day">Day</label>
              <input id="note-day" type="date" value={day} max={todayInput()} onChange={(e) => setDay(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="note-text">Notes</label>
            <textarea
              id="note-text"
              rows={3}
              value={text}
              maxLength={4000}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Antenna snapped in the wind; fitted a new 3 dBi whip and moved it up the pole. Signal now -95 dBm."
            />
          </div>
          {error && <div className="error">{error}</div>}
          <div className="toolbar">
            <button className="btn primary" disabled={busy || (kind === "other" && !text.trim())}>
              {busy ? "Saving…" : "Save note"}
            </button>
            <button type="button" className="btn" onClick={() => (setAdding(false), setError(""))}>
              Cancel
            </button>
          </div>
        </form>
      )}
      <div className="card list">
        {!list.length && <div className="empty">No notes yet. Add one after a site visit or a part is replaced, so the next person knows the device's history.</div>}
        {list.map((n) => (
          <div key={n.id} className="ctl">
            <div style={{ minWidth: 0 }}>
              <div className="t">
                {NOTE_KINDS[n.kind] ?? n.kind} <span className="muted small">· {dayText(n.at)}</span>
              </div>
              {n.text && <div style={{ whiteSpace: "pre-wrap", fontSize: 14.5, marginTop: 2 }}>{n.text}</div>}
              <div className="d">{n.by}</div>
            </div>
            {(staff || n.byId === myId) && (
              <div className="ctl-actions">
                {confirm === n.id ? (
                  <>
                    <button className="btn danger" onClick={() => void remove(n)}>
                      Confirm: delete
                    </button>
                    <button className="btn ghost" onClick={() => setConfirm(null)}>
                      Keep
                    </button>
                  </>
                ) : (
                  <button className="btn ghost" onClick={() => setConfirm(n.id)}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {!adding && error && <div className="error">{error}</div>}
    </Section>
  );
}
